import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import { exportDataTableCsv } from '../../helpers/exportCsv';
import { getSystemsMapsInfo } from '../../stores/actions/installerActions';
import { fetchUrl } from '../../stores/actions/settings';

const DEFAULT_MAP_WIDTH = 1400;
const DEFAULT_MAP_HEIGHT = 1000;

type SizeStatus = 'default' | 'custom';

interface SystemMapInfoDto {
  id: number;
  name: string;
  systemVersion: string;
  mapSizeX: number;
  mapSizeY: number;
  mapDataUpdatedAt: string;
}

interface SystemMapRow {
  id: number;
  name: string;
  systemVersion: string;
  mapSizeX: number;
  mapSizeY: number;
  mapDataUpdatedAt: string;
  sizeStatus: SizeStatus;
}

interface SystemMapsProps {
  systemsMapsInfo: SystemMapInfoDto[];
  getSystemsMapsInfo: () => void;
}

const sizeStatusOptions = [
  { label: 'Default size', value: 'default' },
  { label: 'Custom size', value: 'custom' },
];

const systemVersionFilterOptions = [
  { label: 'WSN', value: 'WSN' },
  { label: 'LoRa', value: 'LoRa' },
];

const createFilters = () => ({
  name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  systemVersion: { value: null, matchMode: FilterMatchMode.EQUALS },
  sizeStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
});

const formatDateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString('sv-SE').replace('T', ' ') : null;

const SystemMaps = ({ systemsMapsInfo, getSystemsMapsInfo }: SystemMapsProps) => {
  const dt = useRef(null);
  const [filters, setFilters] = useState(createFilters());
  const [viewingMap, setViewingMap] = useState<{ id: number; name: string } | null>(null);
  const [mapImageUrl, setMapImageUrl] = useState<string | null>(null);
  const [mapImageLoading, setMapImageLoading] = useState(false);
  const [mapImageError, setMapImageError] = useState(false);

  useEffect(() => {
    getSystemsMapsInfo();
  }, [getSystemsMapsInfo]);

  const rows: SystemMapRow[] = useMemo(() => {
    return (systemsMapsInfo ?? []).map((s) => ({
      id: s.id,
      name: s.name,
      systemVersion: s.systemVersion === 'W2' ? 'WSN' : 'LoRa',
      mapSizeX: s.mapSizeX,
      mapSizeY: s.mapSizeY,
      mapDataUpdatedAt: s.mapDataUpdatedAt,
      sizeStatus: (s.mapSizeX === DEFAULT_MAP_WIDTH && s.mapSizeY === DEFAULT_MAP_HEIGHT ? 'default' : 'custom') as SizeStatus,
    }));
  }, [systemsMapsInfo]);

  const openMapViewer = (row: SystemMapRow) => {
    setViewingMap({ id: row.id, name: row.name });
    setMapImageUrl(null);
    setMapImageError(false);
    setMapImageLoading(true);

    const token = localStorage.getItem('w4-token');
    fetch(`${fetchUrl}/system/system-map-image/${row.id}/main`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((resp) => {
        if (!resp.ok) throw new Error('Failed to load map image');
        return resp.blob();
      })
      .then((blob) => {
        setMapImageUrl(URL.createObjectURL(blob));
        setMapImageLoading(false);
      })
      .catch(() => {
        setMapImageError(true);
        setMapImageLoading(false);
      });
  };

  const closeMapViewer = () => {
    if (mapImageUrl) {
      URL.revokeObjectURL(mapImageUrl);
    }
    setViewingMap(null);
    setMapImageUrl(null);
    setMapImageError(false);
    setMapImageLoading(false);
  };

  const sizeStatusTemplate = (row: SystemMapRow) => (
    <Tag
      value={row.sizeStatus === 'default' ? 'Default size' : 'Custom size'}
      severity={row.sizeStatus === 'default' ? 'warning' : 'success'}
    />
  );

  const sizeStatusFilterElement = (options: any) => (
    <Dropdown value={options.value} options={sizeStatusOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder="Any" showClear className="w-full" />
  );

  const systemVersionFilterElement = (options: any) => (
    <Dropdown value={options.value} options={systemVersionFilterOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder="Any" showClear className="w-full" />
  );

  const actionsTemplate = (row: SystemMapRow) => (
    <Button
      label="View map"
      icon="pi pi-image"
      size="small"
      outlined
      onClick={() => openMapViewer(row)}
    />
  );

  const dataTable = useMemo(() => {
    if (!systemsMapsInfo) return null;
    return (
      <div className="card">
        <div className="flex justify-content-end mb-3">
          <Button label="Export" icon="pi pi-file-export" outlined onClick={() => exportDataTableCsv(dt, 'system-maps')} />
        </div>

        <DataTable ref={dt} value={rows}
          dataKey="id" paginator rows={25} rowsPerPageOptions={[10, 25, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} systems"
          onFilter={(e) => setFilters(e.filters)}
          filters={filters} filterDisplay="row" stripedRows size='small' resizableColumns
          sortField="sizeStatus" sortOrder={-1}
          exportFilename="system-maps"
          exportFunction={(e) => {
            if (e.field === 'sizeStatus') return sizeStatusOptions.find((o) => o.value === e.data)?.label ?? '';
            if (e.field === 'mapDataUpdatedAt') return formatDateTime(e.data) ?? '';
            return e.data ?? '';
          }}>
          <Column field="name" header="System" sortable filter filterPlaceholder="" showFilterMenu={false} />
          <Column field="systemVersion" header="Version" sortable style={{ maxWidth: '130px' }}
            body={(row: SystemMapRow) => <Tag value={row.systemVersion} severity={row.systemVersion === 'WSN' ? 'info' : 'secondary'} />}
            filter filterElement={systemVersionFilterElement} showFilterMenu={false} />
          <Column field="mapSizeX" header="Width" sortable style={{ maxWidth: '110px' }} />
          <Column field="mapSizeY" header="Height" sortable style={{ maxWidth: '110px' }} />
          <Column field="sizeStatus" header="Map size" sortable style={{ maxWidth: '170px' }}
            body={sizeStatusTemplate} filter filterElement={sizeStatusFilterElement} showFilterMenu={false} />
          <Column field="mapDataUpdatedAt" header="Map updated" sortable style={{ maxWidth: '200px' }}
            body={(row: SystemMapRow) => formatDateTime(row.mapDataUpdatedAt)} />
          <Column header="" style={{ maxWidth: '150px' }} body={actionsTemplate} />
        </DataTable>
      </div>
    );
  }, [rows, filters, systemsMapsInfo]);

  return (
    <div>
      {dataTable}
      <Dialog
        visible={!!viewingMap}
        onHide={closeMapViewer}
        header={viewingMap ? `Map - ${viewingMap.name}` : ''}
        style={{ width: '90vw', maxWidth: '1050px' }}
        modal
      >
        {mapImageLoading ? <div>Loading map...</div> : null}
        {mapImageError ? <div>Failed to load map image.</div> : null}
        {mapImageUrl ? <img src={mapImageUrl} alt={viewingMap ? `Map for ${viewingMap.name}` : 'System map'} style={{ maxWidth: '100%', display: 'block' }} /> : null}
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state) => ({
  systemsMapsInfo: state.dashboard.systemsMapsInfo,
});

const actions = { getSystemsMapsInfo };

export default connect(mapStateToProps, actions)(SystemMaps);
